import React, { useState, useEffect } from 'react';
import { aiApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon
} from 'components/icons';
import PageHeader from '../../components/ui/PageHeader';

interface Document {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  url: string;
  source: string;
  sourceId?: string;
  status: string;
  metadata?: {
    noticeId?: string;
    solicitationNumber?: string;
    opportunityTitle?: string;
    agency?: string;
    savedFromOpportunity?: boolean;
    savedAt?: string;
  };
  createdAt: string;
}

const Documents: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Fetch user's documents on mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setIsLoadingDocs(true);
      const response = await aiApi.getDocuments();
      setDocuments(response.data.data || []);
    } catch (error: any) {
      if (error.response?.status === 403) {
        setError('AI Document Hub requires PRO or ENTERPRISE plan');
      } else {
        setError('Failed to load documents');
      }
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setIsLoading(true);
    try {
      const response = await aiApi.queryDocuments({ query });
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return 'Unknown size';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const toggleSelect = (docId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const toggleSelectGroup = (groupDocs: Document[]) => {
    const groupIds = groupDocs.map(d => d.id);
    const allSelected = groupIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        groupIds.forEach(id => next.delete(id));
      } else {
        groupIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await aiApi.bulkDeleteDocuments(Array.from(selectedIds));
      setDocuments(prev => prev.filter(d => !selectedIds.has(d.id)));
      toast.success(`${selectedIds.size} documents deleted`);
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to delete documents');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDelete = async (docId: string) => {
    setDeletingId(docId);
    try {
      await aiApi.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch {
      toast.error('Failed to delete document');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  // Filter documents based on search query
  const filteredDocuments = query.trim()
    ? documents.filter(doc =>
        doc.filename.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.noticeId?.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.solicitationNumber?.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.agency?.toLowerCase().includes(query.toLowerCase()) ||
        doc.metadata?.opportunityTitle?.toLowerCase().includes(query.toLowerCase())
      )
    : documents;

  // Group documents by solicitation (sourceId)
  const groupedDocuments = filteredDocuments.reduce<Record<string, { title: string; solicitation: string; agency: string; docs: Document[] }>>((groups, doc) => {
    const key = doc.sourceId || '_ungrouped';
    if (!groups[key]) {
      groups[key] = {
        title: doc.metadata?.opportunityTitle || 'Unknown Opportunity',
        solicitation: doc.metadata?.solicitationNumber || '',
        agency: doc.metadata?.agency || '',
        docs: [],
      };
    }
    groups[key].docs.push(doc);
    return groups;
  }, {});

  const groupKeys = Object.keys(groupedDocuments).sort((a, b) => {
    if (a === '_ungrouped') return 1;
    if (b === '_ungrouped') return -1;
    // Sort by most recent document in each group
    const aLatest = Math.max(...groupedDocuments[a].docs.map(d => new Date(d.createdAt).getTime()));
    const bLatest = Math.max(...groupedDocuments[b].docs.map(d => new Date(d.createdAt).getTime()));
    return bLatest - aLatest;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Document Hub"
        subtitle="Search and manage your saved documents from opportunities"
      />

      {/* Search */}
      <div className="bg-surface shadow rounded-lg p-6">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-content-subtle" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by filename, notice ID, solicitation number, or agency..."
              className="input pl-10 w-full"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {results.length > 0 && (
          <div className="mt-6 space-y-4">
            {results.map((result, index) => (
              <div key={index} className="p-4 bg-surface-sunk rounded-lg">
                <div className="flex items-center gap-2">
                  <DocumentIcon className="h-5 w-5 text-content-subtle" />
                  <span className="font-medium text-content">{result.title || result.filename}</span>
                </div>
                <p className="mt-2 text-sm text-content-muted">{result.excerpt}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Documents — Grouped by Solicitation */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-title text-content">My Documents</h2>
            <p className="text-sm text-content-muted mt-0.5">
              {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} across {groupKeys.length} solicitation{groupKeys.length !== 1 ? 's' : ''}
            </p>
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
            >
              <TrashIcon className="h-4 w-4" />
              {bulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size} selected`}
            </button>
          )}
        </div>

        {isLoadingDocs ? (
          <div className="bg-surface shadow rounded-lg px-6 py-8 text-center text-content-muted">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4"></div>
            Loading documents...
          </div>
        ) : error ? (
          <div className="bg-surface shadow rounded-lg px-6 py-8 text-center text-red-600">{error}</div>
        ) : filteredDocuments.length === 0 ? (
          <div className="bg-surface shadow rounded-lg px-6 py-8 text-center text-content-muted">
            {query.trim()
              ? 'No documents match your search'
              : 'No documents saved yet. Save documents from opportunity pages to see them here.'}
          </div>
        ) : (
          groupKeys.map((groupKey) => {
            const group = groupedDocuments[groupKey];
            const isCollapsed = collapsedGroups.has(groupKey);
            const processedCount = group.docs.filter(d => d.status === 'processed' || d.status === 'ready').length;

            return (
              <div key={groupKey} className="bg-surface shadow rounded-lg overflow-hidden">
                {/* Group Header */}
                <div className="w-full px-6 py-4 flex items-center gap-3 hover:bg-surface-sunk transition-colors">
                  <input
                    type="checkbox"
                    checked={group.docs.length > 0 && group.docs.every(d => selectedIds.has(d.id))}
                    onChange={() => toggleSelectGroup(group.docs)}
                    className="h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500 flex-shrink-0"
                  />
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="flex items-center justify-between flex-1 min-w-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FolderIcon className="h-6 w-6 text-brand-500 flex-shrink-0" />
                      <div className="text-left min-w-0">
                        <p className="text-sm font-medium text-content truncate">
                          {groupKey === '_ungrouped' ? 'Other Documents' : group.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-content-muted mt-0.5">
                          {group.solicitation && <span className="text-brand-600 font-mono">{group.solicitation}</span>}
                          {group.agency && <span>{group.solicitation ? '•' : ''} {group.agency}</span>}
                          <span>{group.solicitation || group.agency ? '•' : ''} {group.docs.length} file{group.docs.length !== 1 ? 's' : ''}</span>
                          {processedCount > 0 && (
                            <>
                              <span>•</span>
                              <CheckCircleIcon className="h-3 w-3 text-green-500 inline" />
                              <span className="text-green-600">{processedCount} AI Ready</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {isCollapsed ? (
                      <ChevronRightIcon className="h-5 w-5 text-content-subtle flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-content-subtle flex-shrink-0" />
                    )}
                  </button>
                </div>

                {/* Document List */}
                {!isCollapsed && (
                  <div className="border-t border-border divide-y divide-border">
                    {group.docs.map((doc) => (
                      <div key={doc.id} className="px-6 py-3 flex items-center justify-between hover:bg-surface-sunk">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(doc.id)}
                            onChange={() => toggleSelect(doc.id)}
                            className="h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500 flex-shrink-0"
                          />
                          <DocumentIcon className="h-6 w-6 text-blue-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-content truncate">{doc.filename}</p>
                            <div className="flex items-center gap-2 text-xs text-content-muted mt-0.5">
                              <span>{formatFileSize(doc.size)}</span>
                              <span>•</span>
                              <span>{formatDate(doc.createdAt)}</span>
                              {(doc.status === 'processed' || doc.status === 'ready') && (
                                <>
                                  <span>•</span>
                                  <CheckCircleIcon className="h-3 w-3 text-green-500 inline" />
                                  <span className="text-green-600">AI Ready</span>
                                </>
                              )}
                              {doc.status === 'dispatched' && (
                                <>
                                  <span>•</span>
                                  <span className="text-blue-500">Sent to AI</span>
                                </>
                              )}
                              {doc.status === 'error' && (
                                <>
                                  <span>•</span>
                                  <span className="text-red-500">Error</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-4">
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-content-subtle hover:text-content-muted hover:bg-surface-sunk rounded"
                            title="Download"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </a>
                          {confirmDeleteId === doc.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(doc.id)}
                                disabled={deletingId === doc.id}
                                className="px-2 py-1 text-xs text-white bg-red-500 hover:bg-red-600 rounded disabled:opacity-50"
                              >
                                {deletingId === doc.id ? '...' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-2 py-1 text-xs text-content-muted hover:text-content hover:bg-surface-sunk rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(doc.id)}
                              className="p-2 text-content-subtle hover:text-red-500 hover:bg-red-50 rounded"
                              title="Delete document"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Documents;
